import { useCallback, useEffect, useMemo, useState } from 'react'
import siteMap from '../../../siteMap'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store'
import { Alert, Box, Button, Card, CardContent, Chip, Grid, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { usePortfolioClimateData } from '../../../hooks/climateData'
import { EmissionTier, MetricState, CanonicalPeriod, AdaptationView } from '../common'
import { ExtremeWindScoreBadge, scoreColor as extremeWindScorePalette } from '../common/extremeWind/utils'
import { useHistory } from 'react-router-dom'
import { LoadingValue } from './PortfolioLoading'
import PortfolioSummaryMetricCard from './PortfolioSummaryMetricCard'
import PortfolioPropertyBreakdownTable, { type PortfolioMetricSpec } from './PortfolioPropertyBreakdownTable'
import PortfolioPropertyMetricMap, {
  type PortfolioMapColorOption,
  type PortfolioMapRadiusOption,
  type NumericMetricSpec,
  type PopupFieldSpec,
} from './PortfolioPropertyMetricMap'
import { useLocations } from '../../../hooks/locations'
import ClimateSectionTitle from '../common/ClimateSectionTitle'
import TooltipDataTable from '../common/TooltipDataTable'
import {
  BI_DAYS_LABEL,
  DAMAGE_AAL_LABEL,
  FINANCIAL_BREAKDOWN_TITLE,
  formatCompactNumber,
  REVENUE_AAL_LABEL,
  TOTAL_AAL_LABEL,
  sumMetricNode,
} from '../common/financialMetrics'
import {
  formatPropertyCoverage,
  meanAggregateValues,
  sumAggregateValues,
  sumAvailableComponentValues,
} from '../common/portfolioAggregates'
import { useCurrentUser } from '../../../hooks/users'

type PropertySubset = { mode: 'all' } | { mode: 'selected'; ids: number[] }

type ScalarMetricMap = {
  values: Record<number, number | null>
  status: Record<number, MetricState | null>
}

function compactCurrency(value: number | null | undefined, currencySymbol: string) {
  if (value == null || Number.isNaN(value)) return '—'
  return `${currencySymbol}${formatCompactNumber(value, 1)} / year`
}

function fixed1(value: number | null | undefined) {
  if (value == null || Number.isNaN(value)) return '—'
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value)
}

function formatCoverageFraction(available: number, total: number) {
  return `${available} / ${total}`
}

function renderHelpLines(lines: string[]) {
  return (
    <Stack spacing={0.5}>
      {lines.map(line => (
        <Typography key={line} variant="caption">
          {line}
        </Typography>
      ))}
    </Stack>
  )
}

function renderAggregateTooltip(
  summary: { contributors: number; total: number },
  label: string,
  aggregationText: string,
  extraLines: string[] = [],
) {
  return renderHelpLines([
    `${label}: ${aggregationText}.`,
    `Coverage: ${formatPropertyCoverage(summary.contributors, summary.total)}.`,
    ...extraLines,
  ])
}

function pickScalarMetric(entry: any, tierKey: string): number | null {
  if (!entry || entry.state?.value !== MetricState.SUCCEEDED || !entry.data) return null
  return sumMetricNode(entry.data?.data?.[tierKey])
}

export default function PortfolioExtremeWind({
  emissionTier,
  selectedPeriod,
  view,
}: {
  emissionTier: EmissionTier
  selectedPeriod: CanonicalPeriod
  view?: AdaptationView
}) {
  const theme = useTheme()
  const history = useHistory()
  const { groupId } = useSelector((state: RootState) => state.system)
  const { data: locations } = useLocations({ group_id: groupId ?? undefined })
  const numberOfProperties = locations?.length ?? null
  const { currencySymbol } = useCurrentUser()
  const queryParams = useMemo(() => ({ view }), [view])

  const { data: scoreByProperty, isLoading: scoreLoading } = usePortfolioClimateData(
    'tc:score',
    {
      groupId: groupId ?? undefined,
    },
    queryParams,
  )
  const { data: damageAalByProperty, isLoading: damageAalLoading } = usePortfolioClimateData(
    'tc:average_damage_loss',
    {
      groupId: groupId ?? undefined,
    },
    queryParams,
  )
  const { data: revenueAalByProperty, isLoading: revenueAalLoading } = usePortfolioClimateData(
    'tc:average_bi_loss',
    {
      groupId: groupId ?? undefined,
    },
    queryParams,
  )
  const { data: biDaysByProperty, isLoading: biDaysLoading } = usePortfolioClimateData(
    'tc:average_bi_days',
    {
      groupId: groupId ?? undefined,
    },
    queryParams,
  )

  const goToProperty = useCallback(
    (propertyId: number) => history.push(siteMap.PropertyClimateAnalytics.getPath(propertyId, 'tc', emissionTier)),
    [emissionTier, history],
  )

  const [propertySubset, setPropertySubset] = useState<PropertySubset>({ mode: 'all' })
  const [mapClearSelectionToken, setMapClearSelectionToken] = useState(0)

  useEffect(() => {
    setPropertySubset({ mode: 'all' })
    setMapClearSelectionToken(value => value + 1)
  }, [groupId])

  const activePropertyIds = useMemo(() => {
    return propertySubset.mode === 'selected' ? propertySubset.ids : undefined
  }, [propertySubset])

  const handleConfirmSelection = useCallback((propertyIds: number[]) => {
    if (!propertyIds.length) {
      setPropertySubset({ mode: 'all' })
      return
    }
    setPropertySubset({ mode: 'selected', ids: propertyIds })
  }, [])

  const handleClearSelection = useCallback(() => {
    setPropertySubset({ mode: 'all' })
  }, [])

  const clearActiveSubset = useCallback(() => {
    setPropertySubset({ mode: 'all' })
    setMapClearSelectionToken(value => value + 1)
  }, [])

  const tierKey = emissionTier as unknown as string

  const scoreMetric = useMemo<ScalarMetricMap>(() => {
    const values: Record<number, number | null> = {}
    const status: Record<number, MetricState | null> = {}
    if (scoreByProperty && typeof scoreByProperty === 'object') {
      Object.values(scoreByProperty as Record<string, any>).forEach(entry => {
        const propertyId = entry?.property_id
        if (typeof propertyId !== 'number') return
        status[propertyId] = entry.state?.value ?? null
        values[propertyId] = pickScalarMetric(entry, tierKey)
      })
    }
    return { values, status }
  }, [scoreByProperty, tierKey])

  const damageAalMetric = useMemo<ScalarMetricMap>(() => {
    const values: Record<number, number | null> = {}
    const status: Record<number, MetricState | null> = {}
    if (damageAalByProperty && typeof damageAalByProperty === 'object') {
      Object.values(damageAalByProperty as Record<string, any>).forEach(entry => {
        const propertyId = entry?.property_id
        if (typeof propertyId !== 'number') return
        status[propertyId] = entry.state?.value ?? null
        values[propertyId] = pickScalarMetric(entry, tierKey)
      })
    }
    return { values, status }
  }, [damageAalByProperty, tierKey])

  const revenueAalMetric = useMemo<ScalarMetricMap>(() => {
    const values: Record<number, number | null> = {}
    const status: Record<number, MetricState | null> = {}
    if (revenueAalByProperty && typeof revenueAalByProperty === 'object') {
      Object.values(revenueAalByProperty as Record<string, any>).forEach(entry => {
        const propertyId = entry?.property_id
        if (typeof propertyId !== 'number') return
        status[propertyId] = entry.state?.value ?? null
        values[propertyId] = pickScalarMetric(entry, tierKey)
      })
    }
    return { values, status }
  }, [revenueAalByProperty, tierKey])

  const biDaysMetric = useMemo<ScalarMetricMap>(() => {
    const values: Record<number, number | null> = {}
    const status: Record<number, MetricState | null> = {}
    if (biDaysByProperty && typeof biDaysByProperty === 'object') {
      Object.values(biDaysByProperty as Record<string, any>).forEach(entry => {
        const propertyId = entry?.property_id
        if (typeof propertyId !== 'number') return
        status[propertyId] = entry.state?.value ?? null
        values[propertyId] = pickScalarMetric(entry, tierKey)
      })
    }
    return { values, status }
  }, [biDaysByProperty, tierKey])

  const totalAalMetric = useMemo<ScalarMetricMap>(() => {
    const values: Record<number, number | null> = {}
    const status: Record<number, MetricState | null> = {}
    const propertyIds = new Set<number>([
      ...Object.keys(damageAalMetric.values).map(Number),
      ...Object.keys(revenueAalMetric.values).map(Number),
    ])

    propertyIds.forEach(propertyId => {
      const damageAal = damageAalMetric.values[propertyId] ?? null
      const revenueAal = revenueAalMetric.values[propertyId] ?? null
      values[propertyId] =
        typeof damageAal === 'number' || typeof revenueAal === 'number' ? (damageAal ?? 0) + (revenueAal ?? 0) : null

      const damageStatus = damageAalMetric.status[propertyId] ?? null
      const revenueStatus = revenueAalMetric.status[propertyId] ?? null
      status[propertyId] =
        damageStatus === MetricState.SUCCEEDED || revenueStatus === MetricState.SUCCEEDED
          ? MetricState.SUCCEEDED
          : damageStatus ?? revenueStatus
    })

    return { values, status }
  }, [damageAalMetric.status, damageAalMetric.values, revenueAalMetric.status, revenueAalMetric.values])

  const selectedPropertyIds = useMemo(() => {
    if (activePropertyIds) return activePropertyIds
    return (locations ?? []).map((location: any) => location.id)
  }, [activePropertyIds, locations])

  const collectScalarValues = useCallback(
    (metricValues: Record<number, number | null>) =>
      selectedPropertyIds
        .map(propertyId => metricValues[propertyId] ?? null)
        .filter((value): value is number => typeof value === 'number'),
    [selectedPropertyIds],
  )

  const selectedScoreValues = collectScalarValues(scoreMetric.values)
  const selectedDamageAalValues = collectScalarValues(damageAalMetric.values)
  const selectedRevenueAalValues = collectScalarValues(revenueAalMetric.values)
  const selectedBiDaysValues = collectScalarValues(biDaysMetric.values)

  const kpis = useMemo(
    () => ({
      scoreSummary: meanAggregateValues(selectedPropertyIds, propertyId => scoreMetric.values[propertyId] ?? null),
      highCount: selectedScoreValues.filter(value => value >= 4).length,
      totalAalSummary: sumAvailableComponentValues(selectedPropertyIds, {
        damage: propertyId => damageAalMetric.values[propertyId] ?? null,
        revenue: propertyId => revenueAalMetric.values[propertyId] ?? null,
      }),
      damageAalSummary: sumAggregateValues(
        selectedPropertyIds,
        propertyId => damageAalMetric.values[propertyId] ?? null,
      ),
      revenueAalSummary: sumAggregateValues(
        selectedPropertyIds,
        propertyId => revenueAalMetric.values[propertyId] ?? null,
      ),
      biDaysSummary: meanAggregateValues(selectedPropertyIds, propertyId => biDaysMetric.values[propertyId] ?? null),
    }),
    [
      biDaysMetric.values,
      damageAalMetric.values,
      revenueAalMetric.values,
      scoreMetric.values,
      selectedPropertyIds,
      selectedScoreValues,
    ],
  )
  const totalAalHelpText = useMemo(
    () =>
      renderHelpLines([
        `${TOTAL_AAL_LABEL} = Damage AAL + BI AAL.`,
        `Coverage: ${formatPropertyCoverage(kpis.totalAalSummary.contributors, kpis.totalAalSummary.total)}.`,
        `Damage available for ${formatPropertyCoverage(
          kpis.totalAalSummary.componentContributors.damage ?? 0,
          kpis.totalAalSummary.total,
        )}.`,
        `Revenue available for ${formatPropertyCoverage(
          kpis.totalAalSummary.componentContributors.revenue ?? 0,
          kpis.totalAalSummary.total,
        )}.`,
      ]),
    [kpis.totalAalSummary],
  )
  const riskRatingHelpText = useMemo(
    () => renderAggregateTooltip(kpis.scoreSummary, 'Average Risk Rating', 'Average across available risk ratings'),
    [kpis.scoreSummary],
  )
  const highRiskHelpText = useMemo(
    () =>
      renderHelpLines([
        'Count of selected properties rated High or Very High.',
        `Score coverage: ${formatPropertyCoverage(kpis.scoreSummary.contributors, kpis.scoreSummary.total)}.`,
      ]),
    [kpis.highCount, kpis.scoreSummary],
  )
  const damageAalHelpText = useMemo(
    () =>
      renderAggregateTooltip(
        kpis.damageAalSummary,
        DAMAGE_AAL_LABEL,
        'Summed across available structure damage-loss values',
      ),
    [kpis.damageAalSummary],
  )
  const revenueAalHelpText = useMemo(
    () => renderAggregateTooltip(kpis.revenueAalSummary, REVENUE_AAL_LABEL, 'Summed across available BI loss values'),
    [kpis.revenueAalSummary],
  )
  const biDaysHelpText = useMemo(
    () =>
      renderAggregateTooltip(
        kpis.biDaysSummary,
        BI_DAYS_LABEL,
        'Average across available business interruption day values',
      ),
    [kpis.biDaysSummary],
  )

  const tableMetrics = useMemo<PortfolioMetricSpec[]>(
    () => [
      {
        kind: 'score',
        key: 'score',
        label: 'Risk Rating',
        width: 150,
        loading: scoreLoading,
        getValue: propertyId => scoreMetric.values[propertyId] ?? null,
        renderScore: value => <ExtremeWindScoreBadge score={value} size="dense" />,
        status: {
          key: 'statusScore',
          label: 'Status (Risk Rating)',
          hiddenByDefault: true,
          loading: scoreLoading,
          getValue: propertyId => scoreMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'currency',
        key: 'total_aal',
        label: TOTAL_AAL_LABEL,
        width: 170,
        loading: damageAalLoading || revenueAalLoading,
        suffix: '/ year',
        getValue: propertyId => totalAalMetric.values[propertyId] ?? null,
        status: {
          key: 'statusTotalAal',
          label: `Status (${TOTAL_AAL_LABEL})`,
          hiddenByDefault: true,
          loading: damageAalLoading || revenueAalLoading,
          getValue: propertyId => totalAalMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'currency',
        key: 'damage_aal',
        label: DAMAGE_AAL_LABEL,
        width: 170,
        loading: damageAalLoading,
        suffix: '/ year',
        getValue: propertyId => damageAalMetric.values[propertyId] ?? null,
        status: {
          key: 'statusDamageAal',
          label: `Status (${DAMAGE_AAL_LABEL})`,
          hiddenByDefault: true,
          loading: damageAalLoading,
          getValue: propertyId => damageAalMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'currency',
        key: 'revenue_aal',
        label: REVENUE_AAL_LABEL,
        width: 170,
        loading: revenueAalLoading,
        suffix: '/ year',
        getValue: propertyId => revenueAalMetric.values[propertyId] ?? null,
        status: {
          key: 'statusRevenueAal',
          label: `Status (${REVENUE_AAL_LABEL})`,
          hiddenByDefault: true,
          loading: revenueAalLoading,
          getValue: propertyId => revenueAalMetric.status[propertyId] ?? null,
        },
      },
      {
        kind: 'number',
        key: 'bi_days',
        label: BI_DAYS_LABEL,
        width: 160,
        loading: biDaysLoading,
        format: 'fixed1',
        suffix: 'days/year',
        getValue: propertyId => biDaysMetric.values[propertyId] ?? null,
        status: {
          key: 'statusBiDays',
          label: `Status (${BI_DAYS_LABEL})`,
          hiddenByDefault: true,
          loading: biDaysLoading,
          getValue: propertyId => biDaysMetric.status[propertyId] ?? null,
        },
      },
    ],
    [
      biDaysLoading,
      biDaysMetric.status,
      biDaysMetric.values,
      damageAalLoading,
      damageAalMetric.status,
      damageAalMetric.values,
      revenueAalLoading,
      revenueAalMetric.status,
      revenueAalMetric.values,
      scoreLoading,
      scoreMetric.status,
      scoreMetric.values,
      totalAalMetric.status,
      totalAalMetric.values,
    ],
  )

  const scoreNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'score',
      label: 'Risk Rating',
      loading: scoreLoading,
      getValue: propertyId => scoreMetric.values[propertyId] ?? null,
      renderValue: value => <ExtremeWindScoreBadge score={value} size="dense" />,
    }),
    [scoreLoading, scoreMetric.values],
  )

  const totalAalNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'total_aal',
      label: TOTAL_AAL_LABEL,
      loading: damageAalLoading || revenueAalLoading,
      formatKind: 'currency',
      suffix: '/ year',
      getValue: propertyId => totalAalMetric.values[propertyId] ?? null,
    }),
    [damageAalLoading, revenueAalLoading, totalAalMetric.values],
  )

  const damageAalNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'damage_aal',
      label: DAMAGE_AAL_LABEL,
      loading: damageAalLoading,
      formatKind: 'currency',
      suffix: '/ year',
      getValue: propertyId => damageAalMetric.values[propertyId] ?? null,
    }),
    [damageAalLoading, damageAalMetric.values],
  )

  const revenueAalNumericMetric = useMemo<NumericMetricSpec>(
    () => ({
      key: 'revenue_aal',
      label: REVENUE_AAL_LABEL,
      loading: revenueAalLoading,
      formatKind: 'currency',
      suffix: '/ year',
      getValue: propertyId => revenueAalMetric.values[propertyId] ?? null,
    }),
    [revenueAalLoading, revenueAalMetric.values],
  )

  const mapColorOptions = useMemo<PortfolioMapColorOption[]>(
    () => [
      {
        key: 'score',
        label: 'Risk Rating',
        type: 'discrete',
        metric: scoreNumericMetric,
        palette: extremeWindScorePalette,
        labels: {
          1: 'Very Low',
          2: 'Low',
          3: 'Moderate',
          4: 'High',
          5: 'Very High',
        },
        normalize: value => Math.min(Math.max(Math.round(value), 1), 5),
      },
      {
        key: 'total_aal',
        label: TOTAL_AAL_LABEL,
        type: 'continuous',
        metric: totalAalNumericMetric,
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        gradient: {
          stops: [
            { offset: 0, color: '#2ecc71' },
            { offset: 0.5, color: '#f1c40f' },
            { offset: 1, color: '#e74c3c' },
          ],
        },
      },
      {
        key: 'damage_aal',
        label: DAMAGE_AAL_LABEL,
        type: 'continuous',
        metric: damageAalNumericMetric,
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        gradient: {
          stops: [
            { offset: 0, color: '#2ecc71' },
            { offset: 0.5, color: '#f1c40f' },
            { offset: 1, color: '#e74c3c' },
          ],
        },
      },
      {
        key: 'revenue_aal',
        label: REVENUE_AAL_LABEL,
        type: 'continuous',
        metric: revenueAalNumericMetric,
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        gradient: {
          stops: [
            { offset: 0, color: '#2ecc71' },
            { offset: 0.5, color: '#f1c40f' },
            { offset: 1, color: '#e74c3c' },
          ],
        },
      },
    ],
    [damageAalNumericMetric, revenueAalNumericMetric, scoreNumericMetric, totalAalNumericMetric],
  )

  const mapRadiusOptions = useMemo<PortfolioMapRadiusOption[]>(
    () => [
      {
        key: 'total_aal',
        label: TOTAL_AAL_LABEL,
        type: 'scaled',
        metric: totalAalNumericMetric,
        scale: 'sqrt',
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        rangePx: { min: 6, max: 24 },
      },
      {
        key: 'damage_aal',
        label: DAMAGE_AAL_LABEL,
        type: 'scaled',
        metric: damageAalNumericMetric,
        scale: 'sqrt',
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        rangePx: { min: 6, max: 24 },
      },
      {
        key: 'revenue_aal',
        label: REVENUE_AAL_LABEL,
        type: 'scaled',
        metric: revenueAalNumericMetric,
        scale: 'sqrt',
        domain: { type: 'auto', clampPercentiles: [5, 95] },
        rangePx: { min: 6, max: 24 },
      },
    ],
    [damageAalNumericMetric, revenueAalNumericMetric, totalAalNumericMetric],
  )

  const mapPopupFields = useMemo<PopupFieldSpec[]>(
    () => [
      {
        key: 'total_aal',
        label: TOTAL_AAL_LABEL,
        loading: damageAalLoading || revenueAalLoading,
        formatKind: 'currency',
        suffix: '/ year',
        getValue: propertyId => totalAalMetric.values[propertyId] ?? null,
      },
      {
        key: 'damage_aal',
        label: DAMAGE_AAL_LABEL,
        loading: damageAalLoading,
        formatKind: 'currency',
        suffix: '/ year',
        getValue: propertyId => damageAalMetric.values[propertyId] ?? null,
      },
      {
        key: 'revenue_aal',
        label: REVENUE_AAL_LABEL,
        loading: revenueAalLoading,
        formatKind: 'currency',
        suffix: '/ year',
        getValue: propertyId => revenueAalMetric.values[propertyId] ?? null,
      },
      {
        key: 'bi_days',
        label: BI_DAYS_LABEL,
        loading: biDaysLoading,
        decimals: 1,
        suffix: 'days/year',
        getValue: propertyId => biDaysMetric.values[propertyId] ?? null,
      },
    ],
    [
      biDaysLoading,
      biDaysMetric.values,
      damageAalLoading,
      damageAalMetric.values,
      revenueAalLoading,
      revenueAalMetric.values,
      totalAalMetric.values,
    ],
  )

  const annualMotionKey = emissionTier

  return (
    <Stack alignItems="center" pb={6}>
      <Stack sx={{ maxWidth: theme.breakpoints.values?.lg, width: '100%' }} spacing={2}>
        {activePropertyIds && (
          <Alert
            severity="info"
            sx={{ borderRadius: 2 }}
            action={
              <Button color="inherit" size="small" onClick={clearActiveSubset}>
                Clear selection
              </Button>
            }
          >
            <Typography variant="body2" fontWeight={800}>
              Property filter
            </Typography>
            <Typography variant="body2">
              Showing data for a subset of properties ({activePropertyIds.length.toLocaleString()} out of{' '}
              {numberOfProperties?.toLocaleString() ?? '—'}).
            </Typography>
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <PortfolioSummaryMetricCard
              title="Average Risk Rating"
              helpText={riskRatingHelpText}
              loading={scoreLoading}
              transitionKey={annualMotionKey}
            >
              <LoadingValue loading={scoreLoading} variant="rounded" width={72} height={20}>
                <ExtremeWindScoreBadge score={kpis.scoreSummary.value} size="dense" />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <PortfolioSummaryMetricCard
              title="High / Very High Risk Ratings"
              helpText={highRiskHelpText}
              loading={scoreLoading}
              transitionKey={annualMotionKey}
            >
              <LoadingValue loading={scoreLoading} variant="rounded" width={60} height={24}>
                <Chip label={kpis.highCount} />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>

          <Grid item xs={12} md={4}>
            <PortfolioSummaryMetricCard
              title={TOTAL_AAL_LABEL}
              helpText={totalAalHelpText}
              loading={damageAalLoading || revenueAalLoading}
              transitionKey={annualMotionKey}
            >
              <LoadingValue loading={damageAalLoading || revenueAalLoading} variant="rounded" width={140} height={24}>
                <Chip label={compactCurrency(kpis.totalAalSummary.value, currencySymbol)} />
              </LoadingValue>
            </PortfolioSummaryMetricCard>
          </Grid>
        </Grid>

        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <ClimateSectionTitle
                title={FINANCIAL_BREAKDOWN_TITLE}
                subtitle="Damage and revenue components of annualised losses across the selected properties"
              />

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <PortfolioSummaryMetricCard
                    title={DAMAGE_AAL_LABEL}
                    helpText={damageAalHelpText}
                    loading={damageAalLoading}
                    transitionKey={annualMotionKey}
                  >
                    <LoadingValue loading={damageAalLoading} variant="rounded" width={140} height={24}>
                      <Chip label={compactCurrency(kpis.damageAalSummary.value, currencySymbol)} />
                    </LoadingValue>
                  </PortfolioSummaryMetricCard>
                </Grid>

                <Grid item xs={12} md={4}>
                  <PortfolioSummaryMetricCard
                    title={REVENUE_AAL_LABEL}
                    helpText={revenueAalHelpText}
                    loading={revenueAalLoading}
                    transitionKey={annualMotionKey}
                  >
                    <LoadingValue loading={revenueAalLoading} variant="rounded" width={140} height={24}>
                      <Chip label={compactCurrency(kpis.revenueAalSummary.value, currencySymbol)} />
                    </LoadingValue>
                  </PortfolioSummaryMetricCard>
                </Grid>

                <Grid item xs={12} md={4}>
                  <PortfolioSummaryMetricCard
                    title={BI_DAYS_LABEL}
                    helpText={biDaysHelpText}
                    loading={biDaysLoading}
                    transitionKey={annualMotionKey}
                  >
                    <LoadingValue loading={biDaysLoading} variant="rounded" width={120} height={24}>
                      <Chip
                        label={kpis.biDaysSummary.value != null ? `${fixed1(kpis.biDaysSummary.value)} days/year` : '—'}
                      />
                    </LoadingValue>
                  </PortfolioSummaryMetricCard>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>

        <PortfolioPropertyMetricMap
          title="Portfolio Map"
          height={600}
          selectedPeriod={selectedPeriod}
          colorOptions={mapColorOptions}
          radiusOptions={mapRadiusOptions}
          defaultColorKey="score"
          defaultRadiusKey="default"
          popupFields={mapPopupFields}
          onViewProperty={goToProperty}
          onConfirmSelection={handleConfirmSelection}
          onClearSelection={handleClearSelection}
          clearSelectionToken={mapClearSelectionToken}
        />

        <PortfolioPropertyBreakdownTable
          metrics={tableMetrics}
          selectedPeriod={selectedPeriod}
          filterPropertyIds={activePropertyIds}
          onViewProperty={goToProperty}
          height={680}
          initialSort={{ metricKey: 'score', sort: 'desc' }}
        />
      </Stack>
    </Stack>
  )
}
